/*
 * Sweep size guide.
 *
 * Two jobs:
 *   1. Switch every figure in the drawer between centimetres and inches.
 *   2. Take one measurement from the customer and highlight the row it lands in,
 *      so nobody has to read across a printed catalogue page.
 *
 * All data-min / data-max values in the markup are centimetres. When the shopper
 * is typing inches we convert their number to centimetres before comparing, so
 * the chart itself never has to be re-rendered.
 */

if (!customElements.get("size-guide")) {
  const CM_PER_INCH = 2.54;

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      // Inches by default: that is what people here reach for when they pick up
      // a tape measure. Centimetres are one click away for anyone who wants them.
      this.units = "in";

      this.unitButtons = Array.from(this.querySelectorAll("[data-sg-units]"));
      this.unitButtons.forEach((button) => {
        button.addEventListener("click", () => this.setUnits(button.dataset.sgUnits));
      });

      this.querySelectorAll("[data-sg-input]").forEach((input) => {
        input.addEventListener("input", () => this.match(input));
      });
    }

    setUnits(units) {
      if (units !== "cm" && units !== "in") return;
      if (units === this.units) return;

      this.units = units;
      this.classList.toggle("sg-units-cm", units === "cm");
      this.classList.toggle("sg-units-in", units === "in");

      this.unitButtons.forEach((button) => {
        const isActive = button.dataset.sgUnits === units;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      // A number typed in centimetres means something else in inches, so the
      // customer's own figure is converted rather than silently re-read.
      this.querySelectorAll("[data-sg-input]").forEach((input) => {
        const value = parseFloat(input.value);
        if (!isNaN(value)) {
          const converted = units === "in" ? value / CM_PER_INCH : value * CM_PER_INCH;
          input.value = Math.round(converted * 2) / 2;
        }
        this.match(input);
      });
    }

    match(input) {
      const chart = input.closest("[data-sg-chart]");
      if (!chart) return;

      const result = chart.querySelector("[data-sg-result]");
      const rows = Array.from(chart.querySelectorAll("[data-sg-row]"));
      rows.forEach((row) => row.classList.remove("is-match"));

      const typed = parseFloat(input.value);
      if (isNaN(typed) || typed <= 0) {
        if (result) {
          result.textContent = "";
          result.classList.remove("is-miss");
        }
        return;
      }

      const cm = this.units === "in" ? typed * CM_PER_INCH : typed;
      const hit = rows.find((row) => cm >= parseFloat(row.dataset.min) && cm <= parseFloat(row.dataset.max));

      if (!result) return;

      if (hit) {
        hit.classList.add("is-match");
        hit.scrollIntoView({ block: "nearest", behavior: "smooth" });
        result.classList.remove("is-miss");
        result.innerHTML = `${this.text("found")} <strong>${this.escape(hit.dataset.size)}</strong>`;
        return;
      }

      // Sweep's glove chart leaves a band between every size (5,5-6 then 6,5-7),
      // so a perfectly ordinary hand can land on no row at all. Rather than a
      // dead end, point at the next size up and say plainly that it is a
      // suggestion, not Sweep's own figure.
      const smallest = parseFloat(rows[0].dataset.min);
      const largest = parseFloat(rows[rows.length - 1].dataset.max);
      result.classList.add("is-miss");

      if (cm < smallest || cm > largest) {
        result.textContent = this.text("outside");
        return;
      }

      const larger = rows.find((row) => parseFloat(row.dataset.min) > cm);
      if (larger) larger.classList.add("is-match");
      result.textContent = this.text("between").replace("%SIZE%", larger ? larger.dataset.size : "");
    }

    escape(value) {
      const holder = document.createElement("span");
      holder.textContent = value;
      return holder.innerHTML;
    }

    text(key) {
      const strings = this.dataset;
      if (key === "found") return strings.textFound || "Your size:";
      if (key === "outside") return strings.textOutside || "That measurement is outside this chart. Please contact us.";
      return strings.textBetween || "You fall between two sizes. Please contact us.";
    }
  }

  customElements.define("size-guide", SizeGuide);
}
